// src/pages/Home.jsx
import { useState, useEffect } from "react";
import { useNavigate }         from "react-router-dom";
import Nav                     from "../components/Nav";
import Toast                   from "../components/Toast";
import { fetchRecipes }        from "../db";
import { CATEGORIES, getCat }  from "../constants";

export default function Home() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [cat,     setCat]     = useState("all");
  const [toast,   setToast]   = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    fetchRecipes()
      .then(r => { setRecipes(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const displayed = recipes.filter(r => {
    const q = search.toLowerCase();
    const ok = !q || r.title?.includes(q) || (r.tags||"").includes(q) || (r.description||"").includes(q);
    return ok && (cat === "all" || r.category === cat);
  });

  return (
    <div style={{ background:"var(--cream)", minHeight:"100vh" }}>
      <Nav showSearch search={search} setSearch={setSearch} />

      <main style={{ maxWidth:1180, margin:"0 auto", padding:"0 32px 80px" }}>

        {/* Category strip */}
        <div style={s.catBar} className="cat-bar">
          {CATEGORIES.map(c => (
            <button key={c.id} style={{ ...s.catBtn, ...(cat===c.id ? s.catActive : {}) }}
              onClick={() => setCat(c.id)}>
              <span style={s.catRoman}>{c.icon}</span>
              <span style={s.catLabel}>{c.label}</span>
            </button>
          ))}
        </div>

        {/* Divider + count */}
        <div style={s.countRow}>
          <div style={s.countRule} />
          <span style={s.countText}>{displayed.length} מתכונים</span>
          <div style={s.countRule} />
        </div>

        {/* Grid */}
        {loading && <p style={s.loader}>LOADING…</p>}

        {!loading && displayed.length === 0 && (
          <div style={s.empty}>
            <div style={s.emptyRule} />
            <p style={s.emptyTitle}>הארכיון ריק</p>
            <button style={s.emptyBtn} onClick={() => nav("/new")}>הוסף מתכון ראשון</button>
            <div style={s.emptyRule} />
          </div>
        )}

        {!loading && displayed.length > 0 && (
          <div style={s.grid} className="recipes-grid">
            {displayed.map((r, i) => (
              <RecipeCard key={r.id} recipe={r} featured={i === 0}
                onClick={() => nav(`/recipe/${r.id}`)} />
            ))}
          </div>
        )}
      </main>

      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

/* ── Recipe Card ── */
function RecipeCard({ recipe, featured, onClick }) {
  const cat = getCat(recipe.category);
  return (
    <article
      style={{ ...s.card, ...(featured ? s.cardFeatured : {}) }}
      className={`recipe-card${featured ? " card-featured" : ""}`}
      onClick={onClick}
    >
      {/* Image */}
      <div style={{ ...s.cardImgWrap, ...(featured ? s.cardImgFeatured : {}) }}
        className={`card-img${featured ? " card-img-featured" : ""}`}>
        {recipe.imageUrl ? (
          <img src={recipe.imageUrl} alt={recipe.title} style={s.cardImg} />
        ) : (
          <div style={s.cardImgFallback}>
            <span style={s.cardImgIcon}>{cat.icon}</span>
          </div>
        )}
        <div style={s.catBadge}>{cat.label}</div>
      </div>

      {/* Body */}
      <div style={s.cardBody}>
        {recipe.difficulty && <div style={s.diffLabel}>{recipe.difficulty}</div>}
        <h2 style={{ ...s.cardTitle, ...(featured ? s.cardTitleLg : {}) }}>{recipe.title}</h2>
        {recipe.description && (
          <p style={s.cardDesc}>
            {recipe.description.slice(0, featured ? 160 : 90)}
            {recipe.description.length > (featured ? 160 : 90) ? "…" : ""}
          </p>
        )}
        <div style={s.cardFoot}>
          <div style={s.cardMeta}>
            {recipe.cookTime && <span>⏱ {recipe.cookTime}′</span>}
            {recipe.servings  && <span>◎ {recipe.servings} מנות</span>}
          </div>
          {recipe.wine && <span style={s.cardWine}>🍷 {recipe.wine.split("·")[0].trim()}</span>}
        </div>
      </div>
    </article>
  );
}

/* ── Styles ── */
const s = {
  catBar:       { display:"flex", gap:0, overflowX:"auto", borderBottom:"1px solid var(--rule)", marginBottom:0 },
  catBtn:       { padding:"16px 18px", fontSize:25, color:"var(--warm)", letterSpacing:2, textTransform:"uppercase", borderBottom:"2px solid transparent", whiteSpace:"nowrap", transition:"all .2s", display:"flex", flexDirection:"column", alignItems:"center", gap:4 },
  catActive:    { color:"var(--ink)", borderBottom:"2px solid var(--ink)" },
  catRoman:     { fontFamily:"var(--serif)", fontSize:20, fontWeight:400, color:"var(--warm)" },
  catLabel:     { fontSize:18, letterSpacing:2 },
  countRow:     { display:"flex", alignItems:"center", gap:16, margin:"20px 0 28px" },
  countRule:    { flex:1, height:1, background:"var(--rule)" },
  countText:    { fontSize:14, letterSpacing:3, color:"var(--warm)", textTransform:"uppercase", flexShrink:0 },
  loader:       { textAlign:"center", padding:80, fontSize:11, letterSpacing:4, color:"var(--warm)" },
  empty:        { textAlign:"center", padding:"80px 0" },
  emptyRule:    { height:1, background:"var(--rule)", margin:"0 auto 32px", width:100 },
  emptyTitle:   { fontFamily:"var(--serif)", fontSize:20, fontStyle:"italic", color:"var(--warm)", marginBottom:20 },
  emptyBtn:     { background:"var(--ink)", color:"var(--cream)", padding:"10px 28px", fontSize:14, letterSpacing:3, textTransform:"uppercase", cursor:"pointer", fontFamily:"inherit" },
  grid:         { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:24 },
  card:         { background:"var(--card)", cursor:"pointer", boxShadow:"0 2px 12px rgba(0,0,0,0.05)", overflow:"hidden" },
  cardFeatured: { gridColumn:"span 2" },
  cardImgWrap:  { height:200, overflow:"hidden", position:"relative" },
  cardImgFeatured:{ height:340 },
  cardImg:      { width:"100%", height:"100%", objectFit:"cover" },
  cardImgFallback:{ width:"100%", height:"100%", background:"#EDE8E1", display:"flex", alignItems:"center", justifyContent:"center" },
  cardImgIcon:  { fontFamily:"var(--serif)", fontSize:42, color:"var(--rule)" },
  catBadge:     { position:"absolute", top:12, right:12, background:"var(--cream)", fontSize:12, letterSpacing:2, textTransform:"uppercase", padding:"4px 8px" },
  cardBody:     { padding:"20px 24px 24px" },
  diffLabel:    { fontSize:9, letterSpacing:3, color:"var(--burg)", textTransform:"uppercase", marginBottom:8 },
  cardTitle:    { fontFamily:"var(--serif)", fontSize:20, fontWeight:400, lineHeight:1.3, marginBottom:10 },
  cardTitleLg:  { fontSize:30 },
  cardDesc:     { fontSize:13, color:"var(--warm)", lineHeight:1.65, marginBottom:16 },
  cardFoot:     { display:"flex", justifyContent:"space-between", alignItems:"flex-end" },
  cardMeta:     { display:"flex", gap:12, fontSize:11, color:"var(--warm)", letterSpacing:1 },
  cardWine:     { fontSize:11, color:"var(--burg)" },
};
